import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { forwardRef } from 'react';

import onboardingValidationSchemas from '../../../../constants/schemas/onboarding';
import AppInput from '../../../../components/base/input';
import CustomDropdown from '../../../../components/base/custom-dropdown';
import AppTextArea from '../../../../components/base/text-area';
import {
  useAuthStore,
  useBusinessStore,
  useOnboardingStore,
} from '../../../../store';
import GoBack from '../../../../components/base/go-back';

const BusinessInfo = forwardRef(() => {
  const { setBusinessInfo, setBusinessType, businessInfo } =
    useOnboardingStore();

  // Load the auth state from local storage
  const { loadAuthStateFromStorage } = useAuthStore();
  const {
    getBusinessTypes,
    loading: loadingBizStore,
    businessTypes,
  } = useBusinessStore();
  const authState = loadAuthStateFromStorage();
  const { email } = authState || {};

  const [bizTypeTouched, setBizTypeTouched] = useState(false);

  const handleBusinessTypeChange = (value: string) => {
    formik.values.businessType = value;
    setBusinessType(value);
  };

  const formik: any = useFormik({
    initialValues: {
      email: businessInfo.email ? businessInfo.email : email ? email : '',
      description: businessInfo.description,
      website: businessInfo.website,
      registrationNumber: businessInfo.registrationNumber,
      businessType: businessInfo.businessType || '',
    },
    enableReinitialize: true,
    validationSchema: onboardingValidationSchemas[0],
    validateOnMount: true,
    async onSubmit() {},
  });

  useEffect(() => {
    getBusinessTypes();
  }, []);

  useEffect(() => {
    setBusinessInfo(formik.values);
  }, [formik.values]);

  return (
    <form onSubmit={formik.handleSubmit} action="#">
      {/* Title */}
      <div>
        <GoBack text="Go back" path="/start-kyb" />

        <h2 className="text-2xl font-bold text-[#111827]">
          Tell us about your business
        </h2>
        <p className="text-gray-text text-sm">
          We’ll personalize your onboarding based on your responses
        </p>
      </div>

      {/* Form Fields */}
      <div className="grid lg:grid-cols-2 gap-6 mt-8 border rounded-xl p-8">
        {/* Email Address */}
        <div>
          <AppInput
            type="text"
            name="email"
            id="email"
            onChange={formik.handleChange}
            placeholder="Enter your email address"
            required
            label="What is your email address ?"
            onBlur={formik.handleBlur}
            value={formik.values.email}
            errorMessage={
              formik.touched.email && formik.errors.email
                ? formik.errors.email
                : ''
            }
          />
        </div>

        {/* Business Type */}
        <div>
          <CustomDropdown
            label="What type of business do you run?"
            options={businessTypes.map((type) => {
              return {
                name: type,
                label: type.replace(/_/g, ' '),
              };
            })}
            emitObject
            selectedValue={formik.values.businessType}
            onSelectChange={(value) => {
              handleBusinessTypeChange(value.name);
              setBizTypeTouched(true);
            }}
            loading={loadingBizStore}
            required
            errorMessage={
              !formik.values.businessType && bizTypeTouched
                ? 'Business type is required'
                : ''
            }
          />
        </div>

        {/* Website */}
        <div>
          <AppInput
            type="text"
            name="website"
            id="website"
            onChange={formik.handleChange}
            placeholder="Enter your website url eg. http://example.com"
            label="What is your business website (if available)?"
            onBlur={formik.handleBlur}
            value={formik.values.website}
            errorMessage={
              formik.touched.website && formik.errors.website
                ? formik.errors.website
                : ''
            }
          />
        </div>

        {/* Registration number */}
        <div>
          <AppInput
            type="text"
            name="registrationNumber"
            id="registrationNumber"
            placeholder="Enter your registration number"
            required
            label="What is your registration number? (e.g. RC-1234567)"
            inputmask="__-_______"
            useinputmask={true}
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            value={formik.values.registrationNumber}
            errorMessage={
              formik.touched.registrationNumber &&
              formik.errors.registrationNumber
                ? formik.errors.registrationNumber
                : ''
            }
          />
        </div>

        {/* Business Description */}
        <div className="lg:col-span-2">
          <AppTextArea
            name="description"
            id="description"
            label="Briefly describe your business"
            placeholder="Max 1000 words"
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            required
          />
        </div>
      </div>
    </form>
  );
});

// Add displayName for debugging
BusinessInfo.displayName = 'BusinessInfo';

export default BusinessInfo;
